// ControlChannel — frontend client for the agent-server /control WS (W4).
//
// Multiplexed background Claude Code job control with RepoConsole-grade
// reconnect/resume: exponential backoff + jitter, app-level keepalive, a
// staleness watchdog, and per-session lastSeq replay on (re)connect. Mirrors
// RepoConsole.js's proven connection mechanics (same constants, same backoff)
// but speaks the /control protocol instead of /agent.
//
// Public surface (see the README /control protocol section):
//   C->S: hello{} · list{} · dispatch{repo,instruction,label?} ·
//         subscribe{sessionId,lastSeq?} · say{sessionId,text} ·
//         permission{sessionId,id,decision} · interrupt{sessionId} ·
//         end{sessionId} · ping{t}
//   S->C: hello-ok{sessions} · sessions{sessions} ·
//         dispatched{sessionId,label,repo{name,path}} ·
//         event{sessionId,label,payload:<sequenced /agent event>} ·
//         event-transient{sessionId,label,payload} · pong{t} · error{message,ref?}
//
// Emits CustomEvents (this extends EventTarget): 'status', 'sessions',
// 'session-event'. The W4 ready-ownership contract means a freshly dispatched
// session's first 'session-event' payload is {type:'ready',sessionId,repo,seq:1}
// — there is no special handling here, it flows through the generic event path.

// Connection resilience tuning — copied verbatim from RepoConsole.js so the two
// consoles share a single proven reconnect behavior.
const MAX_RECONNECT_ATTEMPTS = 8;
const MAX_RECONNECT_DELAY_MS = 15000;
const CONNECT_TIMEOUT_MS = 10000;
const CLIENT_PING_INTERVAL_MS = 20000;
const STALE_AFTER_MS = 45000;
const STALE_CHECK_INTERVAL_MS = 15000;

const DISPATCH_TIMEOUT_MS = 10000;
const SUBS_KEY = 'controlSubs'; // sessionStorage: JSON {sessionId: lastSeq}

// WS URL builder — mirrors RepoConsole.wsUrlFrom but with pathname '/control'.
// When idToken is provided (W5 tunnel mode) it is appended as &idToken=; the
// server ignores it in localhost mode (ALLOWED_UIDS unset).
function wsUrlFrom(httpUrl, token, idToken) {
  const u = new URL(httpUrl);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.pathname = '/control';
  u.search = `?token=${encodeURIComponent(token)}`;
  if (idToken) u.search += `&idToken=${encodeURIComponent(idToken)}`;
  return u.toString();
}

export default class ControlChannel extends EventTarget {
  constructor({ baseUrl, token, getIdToken } = {}) {
    super();
    this.baseUrl = baseUrl;
    this.token = token;
    this.getIdToken = getIdToken; // optional async () => string (W5 mode)

    this.ws = null;
    this.intentionalClose = false;

    // Reconnect / keepalive / connect-timeout bookkeeping.
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.connectTimeout = null;
    this.pingTimer = null;
    this.staleTimer = null;
    this.lastActivity = 0;

    // Per-session lastSeq, mirrored to sessionStorage so a fresh ControlChannel
    // (e.g. after a CRA reload) re-subscribes from where it left off.
    this.subs = this._loadSubs(); // Map<sessionId, lastSeq>

    // Promise resolvers awaiting their next matching frame.
    this.pendingDispatches = []; // [{resolve, reject, timer}]
    this.pendingLists = []; // [resolve]
  }

  // ---- sessionStorage mirror of the controlSubs map ----
  _loadSubs() {
    try {
      const raw = sessionStorage.getItem(SUBS_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      return new Map(Object.entries(obj).map(([id, seq]) => [id, Number(seq) || 0]));
    } catch {
      return new Map();
    }
  }

  _persistSubs() {
    try {
      const obj = {};
      for (const [id, seq] of this.subs) obj[id] = seq;
      sessionStorage.setItem(SUBS_KEY, JSON.stringify(obj));
    } catch {
      /* noop */
    }
  }

  _emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }

  _setStatus(s) {
    this._emit('status', s);
  }

  _send(obj) {
    const ws = this.ws;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(obj));
    }
  }

  // ---- public API ----
  connect() {
    this.intentionalClose = false;
    this.reconnectAttempts = 0;
    this._connectSocket();
  }

  // Intentional close — no reconnect.
  close() {
    this.intentionalClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._stopTimers();
    this._teardownSocket();
    this._setStatus('closed');
  }

  // dispatch — fire-and-acknowledge. Resolves on the next 'dispatched' frame,
  // rejects on an error frame with ref==='dispatch' or after 10s.
  dispatch({ repoPath, instruction, label }) {
    return new Promise((resolve, reject) => {
      const entry = { resolve, reject, timer: null };
      entry.timer = setTimeout(() => {
        const i = this.pendingDispatches.indexOf(entry);
        if (i !== -1) this.pendingDispatches.splice(i, 1);
        reject(new Error('dispatch timed out'));
      }, DISPATCH_TIMEOUT_MS);
      this.pendingDispatches.push(entry);
      this._send({ type: 'dispatch', repo: repoPath, instruction, label });
    });
  }

  say(sessionId, text) {
    this._send({ type: 'say', sessionId, text });
  }

  permission(sessionId, id, decision) {
    this._send({ type: 'permission', sessionId, id, decision });
  }

  interrupt(sessionId) {
    this._send({ type: 'interrupt', sessionId });
  }

  end(sessionId) {
    this._send({ type: 'end', sessionId });
  }

  // list — resolves on the next 'sessions' frame. hello-ok also carries a
  // sessions array, so a hello during a pending list() resolves it too.
  list() {
    return new Promise((resolve) => {
      this.pendingLists.push(resolve);
      this._send({ type: 'list' });
    });
  }

  // ---- HTTP helpers (repo discovery / status) ----
  async _httpHeaders() {
    const headers = { 'x-agent-token': this.token };
    if (this.getIdToken) {
      // W5: /repos/status requires this in ALLOWED_UIDS mode; the middleware
      // ignores it in localhost mode (harmless on the token-only /repos).
      headers['x-firebase-token'] = await this.getIdToken();
    }
    return headers;
  }

  async fetchRepos() {
    const r = await fetch(`${this.baseUrl}/repos`, { headers: await this._httpHeaders() });
    if (!r.ok) throw new Error(`repos ${r.status}`);
    const data = await r.json();
    return data.repos || [];
  }

  async fetchRepoStatus(path) {
    const r = await fetch(
      `${this.baseUrl}/repos/status?path=${encodeURIComponent(path)}`,
      { headers: await this._httpHeaders() }
    );
    if (!r.ok) throw new Error(`repos/status ${r.status}`);
    return r.json();
  }

  async fetchUsage() {
    const r = await fetch(`${this.baseUrl}/s2s/usage`, { headers: await this._httpHeaders() });
    if (!r.ok) throw new Error(`s2s/usage ${r.status}`);
    return r.json();
  }

  async dispatchResearch(subject, question) {
    const r = await fetch(`${this.baseUrl}/research/dispatch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await this._httpHeaders()) },
      body: JSON.stringify({ subject, query: question }),
    });
    if (!r.ok) throw new Error(`research/dispatch ${r.status}`);
    return r.json();
  }

  // ---- timers ----
  _stopTimers() {
    for (const k of ['pingTimer', 'staleTimer']) {
      if (this[k]) {
        clearInterval(this[k]);
        this[k] = null;
      }
    }
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
  }

  // App-level keepalive (browser WS can't send protocol pings) plus a staleness
  // watchdog: any inbound frame refreshes lastActivity; >45s of silence on an
  // OPEN socket forces a close -> reconnect.
  _startKeepalive() {
    this.pingTimer = setInterval(() => {
      const ws = this.ws;
      if (ws?.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: 'ping', t: Date.now() }));
        } catch {
          /* noop */
        }
      }
    }, CLIENT_PING_INTERVAL_MS);
    this.staleTimer = setInterval(() => {
      const ws = this.ws;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (Date.now() - this.lastActivity > STALE_AFTER_MS) {
        try {
          ws.close();
        } catch {
          /* noop */
        }
      }
    }, STALE_CHECK_INTERVAL_MS);
  }

  // Reconnect with exponential backoff + jitter (0.5-1.0) after an unexpected
  // disconnect — identical math to RepoConsole.scheduleReconnect.
  _scheduleReconnect() {
    if (this.intentionalClose || this.reconnectTimer) return;
    const attempt = this.reconnectAttempts;
    if (attempt >= MAX_RECONNECT_ATTEMPTS) {
      this._setStatus('failed');
      return;
    }
    const base = Math.min(1000 * 2 ** attempt, MAX_RECONNECT_DELAY_MS);
    const delay = Math.round(base * (0.5 + Math.random() * 0.5));
    this.reconnectAttempts = attempt + 1;
    this._setStatus('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._connectSocket();
    }, delay);
  }

  // Detach handlers and close the current socket so its async onclose can't
  // clobber a fresh connection's state.
  _teardownSocket() {
    const old = this.ws;
    if (!old) return;
    old.onopen = old.onmessage = old.onerror = old.onclose = null;
    try {
      old.close();
    } catch {
      /* noop */
    }
    this.ws = null;
  }

  async _connectSocket() {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._teardownSocket();
    this._setStatus(this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting');

    let idToken;
    if (this.getIdToken) {
      try {
        idToken = await this.getIdToken();
      } catch {
        idToken = undefined;
      }
    }

    let ws;
    try {
      ws = new WebSocket(wsUrlFrom(this.baseUrl, this.token, idToken));
    } catch {
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;
    this.connectTimeout = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        try {
          ws.close();
        } catch {
          /* noop */
        }
      }
    }, CONNECT_TIMEOUT_MS);

    ws.onopen = () => {
      if (this.connectTimeout) {
        clearTimeout(this.connectTimeout);
        this.connectTimeout = null;
      }
      this.reconnectAttempts = 0;
      this.lastActivity = Date.now();
      this._setStatus('connected');
      this._startKeepalive();
      // On every (re)open: hello, then re-subscribe each tracked session from
      // its stored lastSeq so missed events replay.
      this._send({ type: 'hello' });
      for (const [sessionId, lastSeq] of this.subs) {
        this._send({ type: 'subscribe', sessionId, lastSeq });
      }
    };

    ws.onmessage = (e) => {
      this.lastActivity = Date.now();
      let data;
      try {
        data = JSON.parse(e.data);
      } catch {
        return;
      }
      try {
        this._handleMessage(data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('ControlChannel message error:', err);
      }
    };

    ws.onerror = () => {
      /* surfaced via onclose -> reconnect */
    };

    ws.onclose = () => {
      if (this.ws !== ws) return; // a newer socket already took over
      this.ws = null;
      this._stopTimers();
      if (this.intentionalClose) {
        this._setStatus('closed');
      } else {
        this._scheduleReconnect();
      }
    };
  }

  // ---- inbound frame routing ----
  _handleMessage(msg) {
    switch (msg.type) {
      case 'hello-ok':
        this._emit('sessions', msg.sessions || []);
        this._resolveLists(msg.sessions || []);
        break;

      case 'sessions':
        this._emit('sessions', msg.sessions || []);
        this._resolveLists(msg.sessions || []);
        break;

      case 'dispatched': {
        const entry = this.pendingDispatches.shift();
        if (entry) {
          clearTimeout(entry.timer);
          entry.resolve({ sessionId: msg.sessionId, label: msg.label, repo: msg.repo });
        }
        break;
      }

      case 'event':
        this._handleSessionEvent(msg, false);
        break;

      case 'event-transient':
        this._handleSessionEvent(msg, true);
        break;

      case 'pong':
        break;

      case 'error':
        if (msg.ref === 'dispatch') {
          const entry = this.pendingDispatches.shift();
          if (entry) {
            clearTimeout(entry.timer);
            entry.reject(new Error(msg.message || 'dispatch error'));
          }
        }
        break;

      default:
        break;
    }
  }

  _resolveLists(sessions) {
    const waiters = this.pendingLists;
    this.pendingLists = [];
    for (const resolve of waiters) resolve(sessions);
  }

  // event / event-transient: track per-session lastSeq, drop already-seen
  // payloads (seq <= lastSeq), then fan out a 'session-event'. The W4 seq-1
  // 'ready' payload flows through here with no special handling.
  _handleSessionEvent(msg, transient) {
    const { sessionId, label, payload } = msg;
    if (sessionId != null && payload && payload.seq != null) {
      const lastSeq = this.subs.get(sessionId) || 0;
      if (payload.seq <= lastSeq) return; // duplicate / already replayed
      this.subs.set(sessionId, payload.seq);
      this._persistSubs();
    }
    this._emit('session-event', { sessionId, label, payload, transient });
  }
}
